// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the legend of a release with a class map (IMERG's rain/snow;
 * releasePhase.ts): one labelled bar per palette, on the one value axis
 * the colour bar already draws, as AYNI 2D does (`luma-legend-image.ts`,
 * stacked ramps sharing an axis).
 *
 *   rain
 *   [▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬]   ← the default palette, as Tools → View draws it
 *   snow
 *   [▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬]   ← the snow class's own palette
 *   0.1    1     10    55
 *                   mm/hr
 *
 * Built on the element `renderColorbar` returns, so its axis, ticks,
 * units, accessible name and click stay what they are. Each bar samples
 * its palette row at the codes the colour bar samples, so the two bars
 * line up value for value with each other and with the globe.
 */

import { buildDisplayLut, releaseCodeAtPosition, type ColorScaleDisplay } from '../services/colorScaleDisplay'
import { phaseTables } from '../services/releasePhase'
import { releaseOf, type ColorScale } from '../types/color-scale'
import type { ReleaseEncoding } from '../types/release-encoding'

const SAMPLES = 32

/** A palette row as a CSS gradient, at the codes the colour bar samples. */
function rowGradient(palette: Uint8Array, row: number, enc: ReleaseEncoding, display: ColorScaleDisplay): string {
  const stops: string[] = []
  for (let i = 0; i < SAMPLES; i++) {
    const position = i / (SAMPLES - 1)
    const code = Math.round(releaseCodeAtPosition(enc, display, position))
    const o = (row * 256 + code) * 4
    stops.push(`rgba(${palette[o]}, ${palette[o + 1]}, ${palette[o + 2]}, ${(palette[o + 3]! / 255).toFixed(3)}) ${(position * 100).toFixed(1)}%`)
  }
  return `linear-gradient(to right, ${stops.join(', ')})`
}

/**
 * Give a colour bar one labelled bar per palette when its release has a
 * class map that colours it; leave it as it is otherwise. Returns the
 * same element.
 */
export function withPhaseBars(bar: HTMLElement, scale: ColorScale, display: ColorScaleDisplay): HTMLElement {
  const release = releaseOf(scale)
  const tables = release ? phaseTables(release, buildDisplayLut(scale, display), display) : null
  const gradient = bar.querySelector<HTMLElement>('.panel-colorbar-gradient')
  if (!release || !tables || !gradient) return bar
  const { aux } = tables
  // Row 0 is every class without a palette of its own (IMERG: rain).
  const plain = aux.classes.filter((c) => !c.stops).map((c) => c.label).filter(Boolean)
  const rows = [
    { label: plain.join(' / ') || null, row: 0 },
    ...aux.classes.filter((c) => c.stops).map((c, k) => ({ label: c.label, row: k + 1 })),
  ]
  bar.classList.add('panel-colorbar-phased')
  const frag = document.createDocumentFragment()
  rows.forEach(({ label, row }, i) => {
    if (label) {
      const caption = document.createElement('div')
      caption.className = 'panel-colorbar-phase-label'
      caption.textContent = label
      frag.appendChild(caption)
    }
    if (i === 0) {
      // The bar renderColorbar drew is the default palette's: keep it.
      frag.appendChild(gradient.cloneNode(true))
      return
    }
    const extra = gradient.cloneNode(false) as HTMLElement
    extra.style.setProperty('--colorbar-ramp', rowGradient(tables.palette, row, release, display))
    frag.appendChild(extra)
  })
  gradient.replaceWith(frag)
  const name = bar.getAttribute('aria-label')
  if (name) bar.setAttribute('aria-label', `${name} (${rows.map((r) => r.label).filter(Boolean).join(', ')})`)
  return bar
}
